import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useFilter } from "../../../hooks";
import "react-datepicker/dist/react-datepicker.css";
import "./DateFilter.css";

type DateFilterProps = {
  name?: string;
  title?: string;
  isRange?: boolean;
  startDateName?: string;
  endDateName?: string;
  className?: string;
  placeholderText?: string;
  open?: boolean;
};

type CalendarHeaderProps = {
  monthDate: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  changeYear: (year: number) => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
};

const ChevronLeft = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ChevronDown = () => (
  <svg
    className="dp-header__chevron"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * تحويل التاريخ من DD-MM-YYYY إلى Date.
 * لا نستخدم new Date(value) مباشرة لأن المتصفح
 * لا يقرأ DD-MM-YYYY بطريقة موثوقة.
 */
const parseDateValue = (value: string | null): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const dateMatch = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);

  if (!dateMatch) {
    return undefined;
  }

  const [, dayValue, monthValue, yearValue] = dateMatch;

  const day = Number(dayValue);
  const month = Number(monthValue);
  const year = Number(yearValue);

  const parsedDate = new Date(year, month - 1, day);

  const isValidDate =
    parsedDate.getFullYear() === year &&
    parsedDate.getMonth() === month - 1 &&
    parsedDate.getDate() === day;

  return isValidDate ? parsedDate : undefined;
};

const formatDateValue = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

const DateFilter: React.FC<DateFilterProps> = ({
  name = "date",
  isRange = false,
  startDateName = "startDate",
  endDateName = "endDate",
  className = "",
  placeholderText,
  open = true,
}) => {
  const { t, i18n } = useTranslation();

  const { handleParamsUpdate, searchParams } = useFilter();

  const selectedDateParam = searchParams.get(name);
  const startDateParam = searchParams.get(startDateName);
  const endDateParam = searchParams.get(endDateName);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() =>
    parseDateValue(selectedDateParam),
  );

  const [startDate, setStartDate] = useState<Date | undefined>(() =>
    parseDateValue(startDateParam),
  );

  const [endDate, setEndDate] = useState<Date | undefined>(() =>
    parseDateValue(endDateParam),
  );

  const datePickerRef = useRef<DatePicker>(null);
 
  useEffect(() => {
    setSelectedDate(parseDateValue(selectedDateParam));
  }, [selectedDateParam]);

  useEffect(() => {
    setStartDate(parseDateValue(startDateParam));
    setEndDate(parseDateValue(endDateParam));
  }, [startDateParam, endDateParam]);
 
  const years = useMemo(
    () => Array.from({ length: 301 }, (_, index) => 1900 + index),
    [],
  );

  const handleDateChange = (date: Date | null) => {
    const newDate = date ?? undefined;

    setSelectedDate(newDate);

    handleParamsUpdate({
      [name]: newDate ? formatDateValue(newDate) : undefined,
    });
  };

  const handleRangeChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;

    const newStartDate = start ?? undefined;
    const newEndDate = end ?? undefined;

    setStartDate(newStartDate);
    setEndDate(newEndDate);
 
    handleParamsUpdate({
      [startDateName]: newStartDate ? formatDateValue(newStartDate) : undefined,
      [endDateName]: newEndDate ? formatDateValue(newEndDate) : undefined,
    });
  };

  const renderHeader = ({
    monthDate,
    decreaseMonth,
    increaseMonth,
    changeYear,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: CalendarHeaderProps) => (
    <div className="dp-header">
      <button
        type="button"
        className="dp-nav"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        aria-label={t("previous_month")}
      >
        <ChevronLeft />
      </button>

      <div className="dp-header__label">
        <span className="dp-header__month">
          {monthDate.toLocaleString(i18n.language || undefined, {
            month: "long",
          })}
        </span>

        <div className="dp-year-select">
          <select
            className="dp-year-select__control"
            value={monthDate.getFullYear()}
            onChange={(event) => {
              changeYear(Number(event.target.value));
            }}
            aria-label={t("select_year")}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <ChevronDown />
        </div>
      </div>

      <button
        type="button"
        className="dp-nav"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        aria-label={t("next_month")}
      >
        <ChevronRight />
      </button>
    </div>
  );

  return (
    <div className={`relative ${className}`} dir="ltr">
      {!isRange ? (
        <div className="relative">
          <DatePicker
            ref={datePickerRef}
            selected={selectedDate}
            onChange={handleDateChange}
            dateFormat="dd-MM-yyyy"
            className="hidden"
            placeholderText={placeholderText || t("select_date")}
            isClearable={false}
            calendarClassName="datepicker-calendar"
            popperClassName="datepicker-popper"
            renderCustomHeader={renderHeader}
            formatWeekDay={(day) => day.slice(0, 3)}
            open={open}
            inline
          />
        </div>
      ) : (
        <div className="relative">
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleRangeChange}
            dateFormat="dd-MM-yyyy"
            className="hidden"
            placeholderText={placeholderText || t("select_date_range")}
            isClearable={false}
            calendarClassName="datepicker-calendar"
            popperClassName="datepicker-popper"
            renderCustomHeader={renderHeader}
            formatWeekDay={(day) => day.slice(0, 3)}
            open={open}
            inline
          />
        </div>
      )}
    </div>
  );
};

export default DateFilter;
